import { useMemo, useState } from "react";
import { Eraser, Trash2 } from "lucide-react";
import {
  ContextMenu,
  ContextMenuRow,
  ContextMenuSeparator,
} from "./ContextMenu";
import { useSignatureSaver } from "../graph/useSignatureSaver";
import {
  useStaticSystemsById,
  useWanderingByClass,
  useWhDestinationByCode,
} from "../hooks/useStaticSystems";
import { CLASS_LABEL } from "../lib/systemClass";
import type { Signature, WhClass } from "../types/chain";

interface SignatureContextMenuProps {
  systemId: string;
  signatures: Signature[];
  scanId: string;
  x: number;
  y: number;
  onClose: () => void;
}

interface CodeRow {
  code: string;
  cls: WhClass | "K162";
}

function buildCodeRows(
  wandering: string[],
  statics: { code: string; class: WhClass }[],
  destinationFor: (code: string) => WhClass | null,
): CodeRow[] {
  const rows: CodeRow[] = [];
  const wandSorted = [...wandering].sort();
  for (const code of wandSorted) {
    rows.push({ code, cls: destinationFor(code) ?? "K162" });
  }
  rows.push({ code: "K162", cls: "K162" });
  const staticSorted = [...statics].sort((a, b) =>
    a.code.localeCompare(b.code),
  );
  for (const s of staticSorted) rows.push({ code: s.code, cls: s.class });
  return rows;
}

function ClassText({ cls }: { cls: WhClass | "K162" }) {
  if (cls === "K162") {
    return (
      <span className="context-menu__wh-class context-menu__wh-class--k162">
        ?
      </span>
    );
  }
  return (
    <span className={`context-menu__wh-class system-class--${cls}`}>
      {CLASS_LABEL[cls]}
    </span>
  );
}

export function SignatureContextMenu({
  systemId,
  signatures,
  scanId,
  x,
  y,
  onClose,
}: SignatureContextMenuProps) {
  const { save, setWhType } = useSignatureSaver();
  const { byId: staticById } = useStaticSystemsById();
  const { forClass } = useWanderingByClass();
  const { forCode: destinationFor } = useWhDestinationByCode();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signature = signatures.find((sig) => sig.scan_id === scanId);
  const staticSystem = staticById(systemId);
  const systemClass: WhClass | null = staticSystem?.class ?? null;

  const codeRows = useMemo<CodeRow[]>(() => {
    if (!signature || signature.group !== "wormhole") return [];
    const wandering = systemClass ? forClass(systemClass) : [];
    const taken = new Set<string>();
    for (const s of signatures) {
      if (s.scan_id === signature.scan_id) continue;
      if (s.group !== "wormhole") continue;
      if (!s.wh_type) continue;
      if (s.wh_type === "K162") continue;
      taken.add(s.wh_type);
    }
    const all = buildCodeRows(
      wandering,
      staticSystem?.statics ?? [],
      destinationFor,
    );
    return all.filter((row) => row.code === "K162" || !taken.has(row.code));
  }, [signature, signatures, systemClass, forClass, staticSystem, destinationFor]);

  const onPickCode = async (code: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await setWhType(systemId, scanId, code);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set wh_type.");
    } finally {
      setBusy(false);
    }
  };

  const onClearCode = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await setWhType(systemId, scanId, null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear wh_type.");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await save(
        systemId,
        signatures.filter((sig) => sig.scan_id !== scanId),
      );
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to remove signature.",
      );
    } finally {
      setBusy(false);
    }
  };

  const isWormhole = signature?.group === "wormhole";
  const hasWhType = isWormhole && signature?.wh_type != null && signature.wh_type !== "";

  return (
    <ContextMenu x={x} y={y} anchor="bottom-right" onClose={onClose}>
      {isWormhole &&
        codeRows.map((row) => (
          <ContextMenuRow
            key={row.code}
            onClick={() => onPickCode(row.code)}
            active={signature?.wh_type === row.code}
            disabled={busy}
          >
            <ClassText cls={row.cls} />
            <span>{row.code}</span>
          </ContextMenuRow>
        ))}
      {hasWhType && (
        <>
          <ContextMenuSeparator />
          <ContextMenuRow onClick={onClearCode} disabled={busy}>
            <Eraser size={14} aria-hidden />
            Clear wh_type
          </ContextMenuRow>
        </>
      )}
      {isWormhole && <ContextMenuSeparator />}
      <ContextMenuRow onClick={onRemove} danger disabled={busy}>
        <Trash2 size={14} aria-hidden />
        {busy ? "Removing…" : "Remove signature"}
      </ContextMenuRow>
      {error && (
        <p className="form__hint form__hint--error" role="alert">
          {error}
        </p>
      )}
    </ContextMenu>
  );
}
